import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/shared/config/queryKeys';
import { AppError } from '@/shared/lib/repository/errors';
import { useAuth } from '@/features/auth';
import type { ReactionType } from '@/shared/types/tables';
import { reactionsRepository } from './reactionsRepository';
import { applyReactionToggle, type ReactionSummary } from '../model/types';

export function useReactions(articleId: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: queryKeys.reactions(articleId),
    queryFn: () => reactionsRepository.getForArticle(articleId, user?.id),
  });
}

/** Toggle the current user's reaction (optimistic). Selecting a new type replaces the old. */
export function useToggleReaction(articleId: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;
  const key = queryKeys.reactions(articleId);

  return useMutation({
    // `remove` is decided by the caller from the rendered state, not the cache —
    // onMutate runs before mutationFn and would otherwise mask the real intent.
    mutationFn: async ({ type, remove }: { type: ReactionType; remove: boolean }) => {
      if (!userId) throw new AppError('Sign in to react');
      if (remove) await reactionsRepository.remove(articleId, userId);
      else await reactionsRepository.set(articleId, userId, type);
    },
    onMutate: async ({ type, remove }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<ReactionSummary>(key);
      queryClient.setQueryData<ReactionSummary>(key, (old) =>
        applyReactionToggle(old, type, remove),
      );
      return { prev };
    },
    onError: (_err, _type, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(key, ctx.prev);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
}
